<template>
  <events-list-page-layout :calendars-filter="[calendar.slug]"
                           :url-filter-keys="urlFilterKeys"
                           :organisation-boundaries="organisationBoundaries"
                           :allowed-advanced-filters="allowedAdvancedFilters"
                           :filter-options="filterOptions"
                           :page-title="calendar.title">
    <template #title>
      <a :href="pagePath">
        <h2> {{ calendar.title }} </h2>
      </a>
    </template>
    <template #share-buttons>
      <social-share-buttons :channels="socialSharing.channels" entity-type="calendar" :entity-slug="calendar.slug"
                            :utm-medium="socialSharing.utmMedium" class="mt-3" />
    </template>
    <template #description>
      <div v-if="partnerships.length > 0" class="mt20">
        <a v-for="partnership in partnerships" :key="partnership.slug" :href="partnership.path">
          <img v-if="partnership.image_url" :src="partnership.image_url" class="partnership-pic">
          <div class="mt10">{{ partnership.title }}</div>
        </a>
      </div>
      <multiline-text v-if="calendar.description" :text="calendar.description" class="mt-3" />
    </template>
    <template #stats>
      <div>
        <stat-with-icon icon-class="icon-users" :number="calendar.attendeesCount" label-i18n-key="activerecord.models.attendee" />
        <stat-with-icon icon-class="icon-star" :number="calendar.eventsCount" label-i18n-key="activerecord.models.event" />
        <stat-with-icon icon-class="icon-star" :number="calendar.upcomingEventsCount" label-i18n-key="upcoming" />
      </div>
    </template>
  </events-list-page-layout>
</template>

<script>
import ajax from '@/helpers/ajax'
import EventsListPageLayout from './events-list-page-layout'
import GeoipCountryMixin from '@/location-search/geoip-country-mixin'
import { mapState } from 'vuex'
import MultilineText from '@/components/multiline-text'
import { sharedMapPropsMixin } from './shared-map-props'
import SocialShareButtons from '@/share/current-page/social-share-buttons'
import { FILTER_KEY_EVENT_TYPES, FILTER_KEY_INCLUDE_VIRTUAL, FILTER_KEY_LABELS, FILTER_KEY_LOCATION,
  FILTER_KEY_REGIONS, FILTER_KEY_START_DATE_RANGE, FILTER_KEY_UPCOMING_ONLY } from './filter-keys'
import StatWithIcon from '../stats/stat-with-icon'
import 'url-search-params-polyfill'

export default {
  components: { EventsListPageLayout, MultilineText, SocialShareButtons, StatWithIcon },

  mixins: [
    GeoipCountryMixin,
    sharedMapPropsMixin // provides searchStrategy, organisationBoundaries, etc.
  ],

  props: {
    calendar: {
      type: Object,
      required: true
    },
    filterOptions: {
      type: Object,
      required: true
    },
    pagePath: {
      type: String,
      required: true
    },
    partnerships: {
      type: Array,
      required: false,
      default: () => []
    },
    showEventTypeFilter: {
      type: Boolean,
      required: true
    },
    showLabelFilter: {
      type: Boolean,
      required: true
    },
    showRegionFilter: {
      type: Boolean,
      required: true
    },
    socialSharing: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      nearbyTargetsWithoutEvents: null,
      urlFilterKeys: [FILTER_KEY_EVENT_TYPES, FILTER_KEY_INCLUDE_VIRTUAL, FILTER_KEY_LABELS, FILTER_KEY_LOCATION,
                      FILTER_KEY_REGIONS, FILTER_KEY_START_DATE_RANGE, FILTER_KEY_UPCOMING_ONLY]
    }
  },

  computed: {
    ...mapState(['eventCreationAllowed', 'eventCreationRequiresSearchedLocation']),

    allowedAdvancedFilters() {
      const allowed = [FILTER_KEY_INCLUDE_VIRTUAL, FILTER_KEY_START_DATE_RANGE, FILTER_KEY_UPCOMING_ONLY]

      if (this.showEventTypeFilter) {
        allowed.push(FILTER_KEY_EVENT_TYPES)
      }

      if (this.showLabelFilter) {
        allowed.push(FILTER_KEY_LABELS)
      }

      if (this.showRegionFilter) {
        allowed.push(FILTER_KEY_REGIONS)
      }

      return allowed
    }
  },

  created() {
    this.$store.commit('setMultipleEventsPerConstituncyAllowed', this.calendar.multipleEventsPerConstituencyAllowed)
    this.$store.commit('setEventCreationAllowed', !this.calendar.eventCreationDisabled)
    this.$store.commit('setEventCreationRequiresSearchedLocation', this.calendar.eventCreationRequiresTarget)
    this.$store.commit('setSearchStrategy', this.searchStrategy)

    // In cases when multiple events per location are not allowed (i.e.: calendars configured this way)
    // we need to prefetch the list of nearby targets that can be used to create a new event
    // to decide whether to show the "New event" button or not. To do this, we subscribe to the
    // store actions and handle `getDetailedLocations`
    if (this.calendar.eventCreationRequiresTarget && !this.calendar.multipleEventsPerConstituencyAllowed) {
      this.$store.subscribe((mutation) => {
        if (mutation.type === 'setSearchedLocation') {
          this.getNearbyTargetsForLocations(mutation.payload)
        }
      })
    }
  },

  methods: {
    getNearbyTargetsForLocations(searchedLocation) {
      if (!searchedLocation) {
        this.nearbyTargetsWithoutEvents = null
        this.$store.commit('setForceCreateEventButtonHidden', true)
        return
      }

      let searchLocationParams = new URLSearchParams('')
      searchLocationParams.append('latitude', searchedLocation.latitude)
      searchLocationParams.append('longitude', searchedLocation.longitude)

      ajax.get({
        url: `/calendars/${this.calendar.slug}/targets/lookup_without_event?${searchLocationParams.toString()}`,
        successHandler: (resp) => {
          this.nearbyTargetsWithoutEvents = resp.data
          this.$store.commit('setForceCreateEventButtonHidden', this.nearbyTargetsWithoutEvents.length === 0)
        }
      })
    }
  }
}
</script>
